import {
	Business as BusinessIcon,
	LocationOn as LocationIcon,
	Person as PersonIcon,
	Home as HomeIcon,
	Public as ProvinceIcon,
	AccountTree as DivisionIcon,
	LocalShipping as DistrictIcon,
	Apartment as TehsilIcon,
	Flag as NationalAssemblyIcon,
	AccountBalance as ProvinceAssemblyIcon,
	Groups as UnionCouncilIcon,
	LocationOn as AddressIcon,
	MyLocation as MyLocationIcon,
	Phone as PhoneIcon,
	PhoneAndroid as MobileIcon,
	Badge as BadgeIcon,
	School as SchoolIcon,
	AccountTree as NetworkIcon,
	CalendarToday as CalendarIcon,
	Email as EmailIcon,
	Book as BookIcon,
	WhatsApp as WhatsAppIcon,
	Numbers as NumbersIcon,
} from "@mui/icons-material";

import BasicInfoStep from "./steps/BasicInfoStep";
import AddressDetailsStep from "./steps/AddressDetailsStep";
import OwnershipDetailsStep from "./steps/OwnershipDetailsStep";
import type { InstituteDetailsData } from "./types/types";
import { getTranslation } from "@utils/translation";

export const instituteSteps = [
	{
		label: getTranslation("components.instituteDetails.steps.basicInfo"),
		icon: <BusinessIcon sx={{ fontSize: 24 }} />,
	},
	{
		label: getTranslation("components.instituteDetails.steps.addressDetails"),
		icon: <LocationIcon sx={{ fontSize: 24 }} />,
	},
	{
		label: getTranslation("components.instituteDetails.steps.ownershipDetails"),
		icon: <PersonIcon sx={{ fontSize: 24 }} />,
	},
];

export const getStepContent = (step: number, data?: InstituteDetailsData) => {
	switch (step) {
		case 0:
			return <BasicInfoStep data={data?.basicInfo} />;
		case 1:
			return <AddressDetailsStep data={data?.addressDetails} />;
		case 2:
			return <OwnershipDetailsStep data={data?.ownershipDetails} />;
		default:
			return "Unknown step";
	}
};

// Default address data removed - using dynamic API data instead

export const addressFieldConfig = [
	{
		name: "address",
		label: getTranslation("components.instituteDetails.address.address"),
		icon: <HomeIcon />,
	},
	{
		name: "location",
		label: getTranslation("components.instituteDetails.address.location"),
		icon: <MyLocationIcon />,
	},
	{
		name: "province",
		label: getTranslation("components.instituteDetails.address.province"),
		icon: <ProvinceIcon />,
	},
	{
		name: "division",
		label: getTranslation("components.instituteDetails.address.division"),
		icon: <DivisionIcon />,
	},
	{
		name: "district",
		label: getTranslation("components.instituteDetails.address.district"),
		icon: <DistrictIcon />,
	},
	{
		name: "tehsil",
		label: getTranslation("components.instituteDetails.address.tehsil"),
		icon: <TehsilIcon />,
	},
	{
		name: "city",
		label: getTranslation("components.instituteDetails.address.city"),
		icon: <LocationIcon />,
	},
	{
		name: "nationalAssembly",
		label: getTranslation("components.instituteDetails.address.nationalAssembly"),
		icon: <NationalAssemblyIcon />,
	},
	{
		name: "provinceAssembly",
		label: getTranslation("components.instituteDetails.address.provinceAssembly"),
		icon: <ProvinceAssemblyIcon />,
	},
	{
		name: "unionCouncil",
		label: getTranslation("components.instituteDetails.address.unionCouncil"),
		icon: <UnionCouncilIcon />,
	},
];

// Default data removed - using dynamic API data instead

export const soleProprietorFieldConfig = [
	{
		name: "soleProprietorName",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorName"),
		icon: <PersonIcon />,
	},
	{
		name: "soleProprietorAddress",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorAddress"),
		icon: <AddressIcon />,
	},
	{
		name: "soleProprietorMobile",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorMobile"),
		icon: <PhoneIcon />,
	},
	{
		name: "soleProprietorLandline",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorLandline"),
		icon: <PhoneIcon />,
	},
	{
		name: "soleProprietorCNIC",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorCNIC"),
		icon: <BadgeIcon />,
	},
	{
		name: "soleProprietorEmail",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorEmail"),
		icon: <EmailIcon />,
	},
	{
		name: "soleProprietorGender",
		label: getTranslation("components.instituteDetails.ownership.soleProprietorGender"),
		icon: <PersonIcon />,
	},
];

export const principleFieldConfig = [
	{
		name: "principleName",
		label: getTranslation("components.instituteDetails.ownership.principleName"),
		icon: <PersonIcon />,
	},
	{
		name: "principleAddress",
		label: getTranslation("components.instituteDetails.ownership.principleAddress"),
		icon: <AddressIcon />,
	},
	{
		name: "principleMobile",
		label: getTranslation("components.instituteDetails.ownership.principleMobile"),
		icon: <PhoneIcon />,
	},
	{
		name: "principleLandline",
		label: getTranslation("components.instituteDetails.ownership.principleLandline"),
		icon: <PhoneIcon />,
	},
	{
		name: "principleCNIC",
		label: getTranslation("components.instituteDetails.ownership.principleCNIC"),
		icon: <BadgeIcon />,
	},
	{
		name: "principleEmail",
		label: getTranslation("components.instituteDetails.ownership.principleEmail"),
		icon: <EmailIcon />,
	},
	{
		name: "principleGender",
		label: getTranslation("components.instituteDetails.ownership.principleGender"),
		icon: <PersonIcon />,
	},
];

// Default basic info data removed - using dynamic API data instead
export const basicInfoFieldConfig = [
	{
		name: "instituteName",
		label: getTranslation("components.instituteDetails.basicInfo.instituteName"),
		icon: <BusinessIcon />,
	},
	{
		name: "instituteLandlineNo",
		label: getTranslation("components.instituteDetails.basicInfo.instituteLandlineNo"),
		icon: <PhoneIcon />,
	},
	{
		name: "instituteMobileNo",
		label: getTranslation("components.instituteDetails.basicInfo.instituteMobileNo"),
		icon: <MobileIcon />,
	},
	{
		name: "ownerCNIC",
		label: getTranslation("components.instituteDetails.basicInfo.ownerCNIC"),
		icon: <BadgeIcon />,
	},
	{
		name: "selectStream",
		label: getTranslation("components.instituteDetails.basicInfo.selectStream"),
		icon: <SchoolIcon />,
	},
	{
		name: "organizationType",
		label: getTranslation("components.instituteDetails.basicInfo.organizationType"),
		icon: <NetworkIcon />,
	},
	{
		name: "whatsapp",
		label: getTranslation("components.instituteDetails.basicInfo.whatsapp"),
		icon: <WhatsAppIcon />,
	},
	{
		name: "dateOfEstablishment",
		label: getTranslation("components.instituteDetails.basicInfo.dateOfEstablishment"),
		icon: <CalendarIcon />,
	},
	{
		name: "emailAddress",
		label: getTranslation("components.instituteDetails.basicInfo.emailAddress"),
		icon: <EmailIcon />,
	},
	{
		name: "technicalNoOfCourses",
		label: getTranslation("components.instituteDetails.basicInfo.technicalNoOfCourses"),
		icon: <BookIcon />,
	},
	{
		name: "vocationalNoOfCourses",
		label: getTranslation("components.instituteDetails.basicInfo.vocationalNoOfCourses"),
		icon: <BookIcon />,
	},
	{
		name: "headName",
		label: getTranslation("components.instituteDetails.basicInfo.headName"),
		icon: <PersonIcon />,
	},
	{
		name: "ntnNumber",
		label: getTranslation("components.instituteDetails.basicInfo.ntnNumber"),
		icon: <NumbersIcon />,
	},
];
